import React from 'react';
import {
  FeatureIcon,
  Link,
  MediaContent,
  StickyContent,
  SubNavigationItem,
} from '~/common/models';
import { CustomCarousel } from '~/common/components/ui-elements';
import styles from './index.module.scss';
import cn from 'classnames';
import { Button, ButtonTypeEnum } from '~/common/components/ui-elements/button';

interface HeaderBannerTilesProp {
  contentId: string;
  mainTileMediaContents: MediaContent[];
  mainTileLegalDisclaimer: string;
  mainTileLegalDisclaimerMobile: string;
  mainTileLegalDisclaimerFontColor: string;
  mainTileLegalDisclaimerAlignment: 'auto' | 'right' | 'left' | 'center';
  mainTileContentText: string;
  mainTileContentFontColor: string;
  mainTileContentBackgroundColor: string;
  mainTileContentCTAEnabled: boolean;
  mainTileContentCTAButtonStyle: keyof typeof ButtonTypeEnum;
  mainTileContentCtabuttonText: string;
  mainTileHyperlink?: string;

  secondaryTileMediaContents: MediaContent[];
  secondaryTileLegalDisclaimer: string;
  secondaryTileLegalDisclaimerMobile: string;
  secondaryTileLegalDisclaimerFontColor: string;
  secondaryTileLegalDisclaimerAlignment: 'auto' | 'right' | 'left' | 'center';
  secondaryTileHyperlink?: string;

  tertiaryTileMediaContents: MediaContent[];
  tertiaryTileLegalDisclaimer: string;
  tertiaryTileLegalDisclaimerMobile: string;
  tertiaryTileLegalDisclaimerFontColor: string;
  tertiaryTileLegalDisclaimerAlignment: 'auto' | 'right' | 'left' | 'center';
  tertiaryTileContentText: string;
  tertiaryTileContentBackgroundColor: string;
  tertiaryTileContentFontColor: string;
  tertiaryTileContentCTAEnabled: boolean;
  tertiaryTileContentCTAButtonStyle: keyof typeof ButtonTypeEnum;
  tertiaryTileContentCtabuttonText: string;
  tertiaryTileHyperlink?: string;
}

const breakpoints = {
  desktop: {
    breakpoint: { max: 4000, min: 768 },
    items: 1,
  },
  mobile: {
    breakpoint: { max: 768, min: 0 },
    items: 1,
  },
};

export default (props: HeaderBannerTilesProp) => {
  const getMatchedDesktopMedia = (mediaContent: MediaContent[]) => {
    return mediaContent.find((media) =>
      media.screenSizes?.some(
        (size: string) =>
          size.includes('XL') || size.includes('LG') || size.includes('SM')
      )
    );
  };

  const getMatchedMobileMedia = (mediaContent: MediaContent[]) => {
    return mediaContent.find((media) =>
      media.screenSizes?.some((size: string) => size.includes('XS'))
    );
  };

  const getFitClassName = (
    baseClassName: string,
    imageFit?: 'cover' | 'contain'
  ): string =>
    cn(baseClassName, {
      [styles.fitContain]: imageFit === 'contain',
      [styles.fitCover]: imageFit !== 'contain',
    });

  const mainTileDesktopMedia = getMatchedDesktopMedia(
    props.mainTileMediaContents
  );
  const secondaryTileDesktopMedia = getMatchedDesktopMedia(
    props.secondaryTileMediaContents
  );
  const tertiaryTileDesktopMedia = getMatchedDesktopMedia(
    props.tertiaryTileMediaContents
  );

  return (
    <section className={styles.headerTileGridContainer}>
      <div className={styles.innerDesktopContainer}>
        <div className={styles.gridLeftSection}>
          <div className={styles.card}>
            <a className={styles.imgLink} href={props.mainTileHyperlink}>
              <img
                src={mainTileDesktopMedia?.mediaSource}
                className={getFitClassName(
                  styles.imgContainer,
                  mainTileDesktopMedia?.imageFit
                )}
              />
            </a>
            <div className={cn(styles.legalDisclaimer, styles.leftTop)}>
              <p data-field="legalDisclaimer">
                <div
                  className={cn(
                    styles.mainDisclaimerTextStyle,
                    styles[
                      `${props.mainTileLegalDisclaimerAlignment}Disclaimer`
                    ]
                  )}
                  style={{ color: props.mainTileLegalDisclaimerFontColor }}
                  dangerouslySetInnerHTML={{
                    __html: String(props.mainTileLegalDisclaimer),
                  }}
                />
              </p>
            </div>
            <div
              className={styles.cardContent}
              style={{
                backgroundColor: props.mainTileContentBackgroundColor,
              }}
            >
              <div
                className={styles.contentText}
                style={{
                  color: props.mainTileContentFontColor,
                }}
                dangerouslySetInnerHTML={{
                  __html: `${props.mainTileContentText}` as string,
                }}
              />
              {props.mainTileContentCTAEnabled && (
                <Button
                  style={{ marginTop: 12 }}
                  type={props.mainTileContentCTAButtonStyle}
                  href={props.mainTileHyperlink}
                  text={props.mainTileContentCtabuttonText}
                />
              )}
            </div>
          </div>
        </div>
        <div className={styles.gridRightSection}>
          <div className={`${styles.card} ${styles.topCard}`}>
            <a
              href={props.secondaryTileHyperlink}
              rel="noreferrer"
              className={styles.imgLink}
            >
              <img
                src={secondaryTileDesktopMedia?.mediaSource}
                className={getFitClassName(
                  styles.imgContainerRight,
                  secondaryTileDesktopMedia?.imageFit
                )}
              />
            </a>
            <div className={cn(styles.legalDisclaimer)}>
              <p data-field="legalDisclaimer">
                <div
                  className={cn(
                    styles.disclaimerTextStyle,
                    styles[
                      `${props.secondaryTileLegalDisclaimerAlignment}Disclaimer`
                    ]
                  )}
                  style={{ color: props.secondaryTileLegalDisclaimerFontColor }}
                  dangerouslySetInnerHTML={{
                    __html: String(props.secondaryTileLegalDisclaimer),
                  }}
                />{' '}
              </p>
            </div>
          </div>
          <div className={styles.card}>
            <a className={styles.imgLink} href={props.tertiaryTileHyperlink}>
              <img
                src={tertiaryTileDesktopMedia?.mediaSource}
                className={getFitClassName(
                  styles.imgContainerRight,
                  tertiaryTileDesktopMedia?.imageFit
                )}
              />
            </a>
            <div className={cn(styles.legalDisclaimer, styles.rightBottom)}>
              <p data-field="legalDisclaimer">
                {' '}
                <div
                  className={cn(
                    styles.disclaimerTextStyle,
                    styles[
                      `${props.tertiaryTileLegalDisclaimerAlignment}Disclaimer`
                    ]
                  )}
                  style={{ color: props.tertiaryTileLegalDisclaimerFontColor }}
                  dangerouslySetInnerHTML={{
                    __html: String(props.tertiaryTileLegalDisclaimer),
                  }}
                />
              </p>
            </div>
            <div
              className={styles.cardContent}
              style={{
                backgroundColor: props.tertiaryTileContentBackgroundColor,
              }}
            >
              <div
                className={styles.rightContentText}
                style={{
                  color: props.tertiaryTileContentFontColor,
                }}
                dangerouslySetInnerHTML={{
                  __html: `${props.tertiaryTileContentText}` as string,
                }}
              />

              {props.tertiaryTileContentCTAEnabled && (
                <Button
                  style={{ marginTop: 12 }}
                  type={props.tertiaryTileContentCTAButtonStyle}
                  href={props.tertiaryTileHyperlink}
                  text={props.tertiaryTileContentCtabuttonText}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.homepageTilesSlider}>
        <CustomCarousel
          responsive={breakpoints}
          showDots
          removeArrowOnDeviceType={['mobile']}
          arrows={false}
        >
          {[
            {
              key: '1',
              href: props.mainTileHyperlink,
              img: getMatchedMobileMedia(props.mainTileMediaContents),
              disclaimer: props.mainTileLegalDisclaimerMobile,
              align: props.mainTileLegalDisclaimerAlignment,
              color: props.mainTileLegalDisclaimerFontColor,
            },
            {
              key: '2',
              href: props.secondaryTileHyperlink,
              img: getMatchedMobileMedia(props.secondaryTileMediaContents),
              disclaimer: props.secondaryTileLegalDisclaimerMobile,
              align: props.secondaryTileLegalDisclaimerAlignment,
              color: props.secondaryTileLegalDisclaimerFontColor,
            },
            {
              key: '3',
              href: props.tertiaryTileHyperlink,
              img: getMatchedMobileMedia(props.tertiaryTileMediaContents),
              disclaimer: props.tertiaryTileLegalDisclaimerMobile,
              align: props.tertiaryTileLegalDisclaimerAlignment,
              color: props.tertiaryTileLegalDisclaimerFontColor,
            },
          ].map(({ key, href, img, disclaimer, align, color }) => (
            <div key={key}>
              <a className={styles.imgLink} href={href}>
                <img
                  src={img?.mediaSource}
                  className={getFitClassName('', img?.imageFit)}
                />
              </a>
              <div
                className={cn(
                  styles.legalDisclaimer,
                  styles[`${align}Disclaimer`]
                )}
              >
                <div
                  data-field="legalDisclaimer"
                  className={styles.mobileDisclaimerText}
                  style={{
                    color,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: `${disclaimer}` as string,
                  }}
                />
              </div>
            </div>
          ))}
        </CustomCarousel>
      </div>
    </section>
  );
};
